/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import { ControlType, ISettingGroupDefinition } from "@nuance/hierarchical-settings-ui-domain";
import { DomainDataMapperCheckbox } from "./DomainDataMapperCheckbox";
import { DomainDataMapperDropdown } from "./DomainDataMapperDropdown";
import { DomainDataMapperHeading } from "./DomainDataMapperHeading";
import { DomainDataMapperRadio } from "./DomainDataMapperRadio";
import { DomainDataMapperSectionHeading } from "./DomainDataMapperSectionHeading";
import { DomainDataMapperSpin } from "./DomainDataMapperSpin";
import { DomainDataMapperText } from "./DomainDataMapperText";
import { DomainDataMapperToggle } from "./DomainDataMapperToggle";
import { IDomainDataMapper } from "./IDomainDataMapper";

export class DomainDataMapperFactory {
    /**
     * Create a domain data mapper for a type of control.
     * @param settingDefinition Setting group definition
     * @param controlType Control type
     */
    static Create(
        settingDefinition: ISettingGroupDefinition,
        controlType: ControlType
    ): IDomainDataMapper;

    /**
     * Create a domain data mapper for a specific control.
     * @param settingDefinition Setting group definition
     * @param controlGuid Control guid
     */
    static Create(
        settingDefinition: ISettingGroupDefinition,
        controlGuid: string
    ): IDomainDataMapper;

    /**
     * Overloaded implementation
     * @param settingDefinition Setting group definition
     * @param a
     * @returns
     */
    static Create(
        settingDefinition: ISettingGroupDefinition,
        a: ControlType | string
    ): IDomainDataMapper | undefined {
        const controlType =
            typeof a === "string"
                ? DomainDataMapperFactory.findControlType(settingDefinition, a)
                : a;

        switch (controlType) {
            case ControlType.TextField:
                return new DomainDataMapperText(settingDefinition);

            case ControlType.NumberField:
                return new DomainDataMapperSpin(settingDefinition);

            case ControlType.DropdownField:
                return new DomainDataMapperDropdown(settingDefinition);

            case ControlType.CheckboxField:
                return new DomainDataMapperCheckbox(settingDefinition);

            case ControlType.RadioField:
                return new DomainDataMapperRadio(settingDefinition);

            case ControlType.ToggleField:
                return new DomainDataMapperToggle(settingDefinition);

            case ControlType.Heading:
                return new DomainDataMapperHeading(settingDefinition);

            case ControlType.SectionHeading:
                return new DomainDataMapperSectionHeading(settingDefinition);

            default:
                throw new Error(
                    `Error creating data mapper, unhandled ControlType: ${controlType}`
                );
        }
    }

    /**
     * Search for a specific control in a setting definition
     * @param definition Setting group definition
     * @param ctrlGuid CtrlGuid to find
     * @returns ControlType of the control
     */
    private static findControlType = (definition: ISettingGroupDefinition, ctrlGuid: string) => {
        const control = definition.UI.Controls.find(i => i.CtrlGuid === ctrlGuid);
        if (control === undefined) {
            throw new Error("Control not found");
        }
        return control.ControlDiscriminator;
    };
}
